import React, { useState, useCallback, useEffect } from 'react';
import { Text, Stack, Group, Button, Container, Title, Alert, Select } from '@mantine/core';
import { Dropzone, FileWithPath } from '@mantine/dropzone';
import { notifications } from '@mantine/notifications';
import { Link } from 'react-router-dom';
import type { AxiosProgressEvent } from 'axios';
import RestaurantSelector from '../../../components/upload/RestaurantSelector';
import ProgressDisplay from '../../../components/upload/ProgressDisplay';
import StatusBadge from '../../../components/upload/StatusBadge';
import { useActiveUploadSession } from '../../../context/ActiveUploadSessionContext';
import { prepareUploadFormData, validateUploadForm, UploadFormFields, RestaurantOption } from '../../../utils/uploadFormData';
import api from '../../../services/api';

/**
 * Admin v2 Upload — early DB write + translation with background + ingredient extraction + categorization.
 */
export function AdminV2Upload() {
  const [files, setFiles] = useState<FileWithPath[]>([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState<string | null>(null);
  const [newRestaurantName, setNewRestaurantName] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [description, setDescription] = useState('');
  const [sourceLanguage, setSourceLanguage] = useState<string>('jp');
  const [loading, setLoading] = useState(false);
  const [restaurants, setRestaurants] = useState<RestaurantOption[]>([]);
  const [requestUploadProgress, setRequestUploadProgress] = useState<number | null>(null);

  const uploadMode = 'v2';

  const {
    startSession,
    clearSession,
    uploadStatus,
    uploadProgress,
    currentFileStatus,
    currentFileIndex,
    detailedStatus,
    currentStage,
    processedFiles,
    uploadResponse,
    clientId
  } = useActiveUploadSession();

  useEffect(() => {
    if (uploadResponse) {
      setFiles([]);
      setLoading(false);
    }
  }, [uploadResponse]);

  useEffect(() => {
    if (uploadStatus === 'Upload failed') {
      setLoading(false);
    }
  }, [uploadStatus]);

  const handleFileDrop = (droppedFiles: FileWithPath[]) => {
    setFiles(prev => [...prev, ...droppedFiles]);
  };

  const removeFile = (indexToRemove: number) => {
    setFiles(files.filter((_, index) => index !== indexToRemove));
  };

  const handleUpload = useCallback(async () => {
    const formFields: UploadFormFields = {
      newRestaurantName,
      adminNotes,
      description,
      uploadMode,
      sourceLanguage
    };

    const validation = validateUploadForm(selectedRestaurant, files, formFields);
    if (!validation.isValid) {
      notifications.show({
        title: 'Error',
        message: validation.error,
        color: 'red',
      });
      return;
    }

    setLoading(true);
    clearSession();
    setRequestUploadProgress(0);

    try {
      const formData = prepareUploadFormData(
        files,
        'menuImages',
        selectedRestaurant,
        restaurants,
        formFields
      );

      const response = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (event: AxiosProgressEvent) => {
          if (!event.total) {
            setRequestUploadProgress(0);
            return;
          }
          const percent = Math.round((event.loaded / event.total) * 100);
          setRequestUploadProgress(Math.max(0, Math.min(100, percent)));
        }
      });

      setRequestUploadProgress(100);
      if (response.data?.clientId) {
        startSession(response.data.clientId);
      }
    } catch (error: any) {
      console.error('Error uploading menu:', error);
      setLoading(false);
      setRequestUploadProgress(null);
      notifications.show({
        title: 'Error',
        message: error.response?.data?.message || 'Failed to upload menu',
        color: 'red',
      });
    }
  }, [selectedRestaurant, files, newRestaurantName, adminNotes, description, sourceLanguage, restaurants, clearSession, startSession]);

  const getStatusBadge = (status: string) => <StatusBadge status={status} />;

  const showProgress = loading || !!clientId;

  return (
    <Container>
      <Title order={1} mb="md">
        v2 Upload Menu by Photo
      </Title>

      <Alert color="violet" mb="md">
        <Text size="sm">
          <strong>v2 Upload Mode:</strong> Saves items immediately after OCR, then runs translation
          (with background context), ingredient extraction linked to the centralized ingredients table,
          and category processing. Balances speed with richer data than fast mode.
        </Text>
      </Alert>

      <Dropzone
        onDrop={handleFileDrop}
        accept={{
          'image/*': ['.png', '.jpg', '.jpeg', '.gif'],
          'application/pdf': ['.pdf'],
        }}
        mb="md"
      >
        <Text ta="center" size="lg">
          Drag and drop menu files here, or click to select files
        </Text>
        <Text ta="center" size="sm" c="dimmed" mt={7}>
          Attach as many files as you like, each file should not exceed 5mb
        </Text>
      </Dropzone>

      {files.length > 0 && (
        <Stack mb="md">
          <Text fw={500}>Files to upload:</Text>
          {files.map((file, index) => (
            <Group key={index} justify="space-between" wrap="nowrap">
              <Text size="sm" style={{ wordBreak: 'break-all' }}>
                {file.name}
              </Text>
              <Button
                variant="subtle"
                color="red"
                size="xs"
                onClick={() => removeFile(index)}
              >
                Remove
              </Button>
            </Group>
          ))}
        </Stack>
      )}

      <RestaurantSelector
        selectedRestaurant={selectedRestaurant}
        onRestaurantChange={setSelectedRestaurant}
        newRestaurantName={newRestaurantName}
        onNewRestaurantNameChange={setNewRestaurantName}
        adminNotes={adminNotes}
        onAdminNotesChange={setAdminNotes}
        description={description}
        onDescriptionChange={setDescription}
        loading={loading}
        onRestaurantsChange={setRestaurants}
      />

      <Stack gap="md" mb="md">
        <Text size="sm" fw={500}>Source Language</Text>
        <Text size="xs" c="dimmed">
          The original language of the menu items. Defaults to Japanese.
        </Text>
        <Select
          value={sourceLanguage}
          onChange={(value) => setSourceLanguage(value || 'jp')}
          disabled={loading}
          data={[
            { value: 'jp', label: 'Japanese' },
            { value: 'ko', label: 'Korean' },
            { value: 'zh', label: 'Chinese' },
            { value: 'en', label: 'English' },
          ]}
        />
      </Stack>

      <ProgressDisplay
        loading={showProgress}
        uploadStatus={uploadStatus}
        uploadProgress={uploadProgress}
        currentFileStatus={currentFileStatus}
        currentFileIndex={currentFileIndex}
        totalFiles={files.length}
        currentStage={currentStage}
        detailedStatus={detailedStatus}
        requestUploadProgress={requestUploadProgress}
      />

      {uploadResponse && (
        <Stack mb="md">
          <Alert title="Upload Complete" color="green">
            <Stack gap="xs">
              <Text>
                <strong>{uploadResponse.restaurantName}</strong> - {uploadResponse.menuName}
              </Text>
              <Text size="sm">
                Successfully processed {uploadResponse.filesProcessed || uploadResponse.itemsProcessed} file(s)
              </Text>
              <Text mt="xs">
                <Link to={`/menus/v2/${uploadResponse.menuId}`}>
                  View the uploaded menu
                </Link>
              </Text>
            </Stack>
          </Alert>

          {processedFiles.length > 0 && (
            <Stack gap="xs">
              {processedFiles.map((file, index) => (
                <Group key={index} justify="space-between">
                  <Text size="sm">{file.originalName}</Text>
                  <Group gap="xs">
                    {getStatusBadge(file.status)}
                    {file.status === 'complete' && (
                      <Text size="sm" c="dimmed">
                        {file.itemsExtracted || file.itemsProcessed} items
                      </Text>
                    )}
                  </Group>
                </Group>
              ))}
            </Stack>
          )}
        </Stack>
      )}

      <Button
        onClick={handleUpload}
        loading={loading}
        disabled={files.length === 0 || !selectedRestaurant || loading}
        fullWidth
      >
        {loading ? 'Uploading...' : 'Upload Files (v2 Mode)'}
      </Button>
    </Container>
  );
}
